import { DocService } from '@affine/core/modules/doc';
import { WorkspaceViewContextService } from '@affine/core/modules/streamlain/copilot/workspace-view-context';
import { ViewService } from '@affine/core/modules/workbench';
import { I18n } from '@affine/i18n';
import {
  type ViewExtensionContext,
  ViewExtensionProvider,
} from '@blocksuite/affine/ext-loader';
import { EdgelessDockSinkExtension } from '@blocksuite/affine/widgets/edgeless-toolbar';
import { FrameworkProvider } from '@toeverything/infra';
import { z } from 'zod';

import {
  DocumentContextDock,
  DocumentContextDockIdentifier,
} from './document-dock';

const optionsSchema = z.object({
  framework: z.instanceof(FrameworkProvider).optional(),
  isInPeekView: z.boolean().optional(),
});

type ContextDockViewOptions = z.infer<typeof optionsSchema>;

/** The document's dock in the view it is open in; null outside of a workbench view. */
function documentDock(framework: FrameworkProvider) {
  const view = framework.getOptional(ViewService)?.view;
  const doc = framework.getOptional(DocService)?.doc;
  const context = framework.getOptional(WorkspaceViewContextService);
  if (!view || !doc || !context) return null;
  return new DocumentContextDock(
    source =>
      context.provideDock(
        { workspaceId: context.workspaceId, viewId: view.id },
        `document:${doc.id}`,
        source
      ),
    () => I18n.t('com.streamlain.framePanel.presentation')
  );
}

export class ContextDockViewExtension extends ViewExtensionProvider<ContextDockViewOptions> {
  override name = 'streamlain-context-dock';

  override schema = optionsSchema;

  override setup(
    context: ViewExtensionContext,
    options?: ContextDockViewOptions
  ) {
    super.setup(context, options);
    const framework = options?.framework;
    // Peek views and previews keep their toolbars without a dock; the island belongs to the view.
    if (!framework || options?.isInPeekView || this.isPreview(context.scope)) {
      return;
    }
    const dock = documentDock(framework);
    if (!dock) return;
    // The editor publishes what only it can do (e.g. taking photos into the canvas).
    context.register({
      setup: di => {
        di.addImpl(DocumentContextDockIdentifier, () => dock);
      },
    });
    if (this.isEdgeless(context.scope)) {
      context.register(
        EdgelessDockSinkExtension({
          publish: tools => dock.publishTools(tools),
        })
      );
    }
  }
}
